// File: src/pages/client/AppointmentsPage.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isBefore } from 'date-fns';
import {
  getAppointmentBucket,
  getAppointmentEndTime,
  getSortDirection,
  APPOINTMENT_BUCKETS,
} from '../../utils/getAppointmentBucket';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { fetchAppointments, updateAppointmentStatus } from '../../store/slices/appointmentsSlice';
import { useDocumentVisibleRefetch } from '../../hooks/useDocumentVisibleRefetch';
import appointmentService from '../../services/appointmentService';
import { formatCancellationConfirmBody } from '../../utils/cancellationConfirmCopy';
import { formatCurrency, formatAppointmentDateTime } from '../../utils/formatters';
import { Appointment } from '../../types/appointment';
import { publicAppointmentRef } from '../../utils/publicRefs';
import {
  CalendarIcon,
  TimeIcon,
  PersonIcon,
  RefreshIcon,
  AlertIcon,
  EmailIcon
} from '../../components/common/Icons';
import { getConversationsWithUnreadCounts, getConversations, getConversation } from '../../services/chatService';
import { SkeletonList } from '../../components/common/SkeletonCard';
import DiscountBadge from '../../components/common/DiscountBadge';

// Placeholder avatar for missing stylist photos
const avatarPlaceholder = 'https://via.placeholder.com/40?text=%F0%9F%91%A4';
const unknownStylistName = 'Unknown Stylist';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...rest }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`appointments-tabpanel-${index}`}
    aria-labelledby={`appointments-tab-${index}`}
    className={`appointments__tab-panel ${value === index ? 'appointments__tab-panel--active' : ''}`}
    {...rest}
  >
    {children}
  </div>
);

const AppointmentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { appointments, isLoading, error } = useAppSelector(s => s.appointments);
  const { user } = useAppSelector(s => s.auth);

  const [tabValue, setTabValue] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  // Fetch unread message counts for appointments (same logic as mobile)
  const fetchUnreadCounts = useCallback(async () => {
    const userId = user?.id || (user as any)?._id;
    if (!userId) return;
    
    try {
      // Get all conversations with unread counts (optimized backend endpoint)
      const conversations = await getConversationsWithUnreadCounts();
      // Filter to only "appointment" type conversations
      const appointmentConvos = conversations.filter(
        (c) => c.type === 'appointment' && c.appointment
      );

      const newCounts: Record<string, number> = {};

      // Map unread counts to appointment IDs
      for (const convo of appointmentConvos) {
        if (convo.appointment && convo.unreadCount !== undefined) {
          newCounts[convo.appointment] = convo.unreadCount;
        }
      }

      setUnreadCounts(newCounts);
    } catch (err) {
      console.error('Error fetching unread message counts:', err);
      // Fallback to the old method if the optimized endpoint fails
      try {
        const conversations = await getConversations();
        const appointmentConvos = conversations.filter(
          (c) => c.type === 'appointment' && c.appointment
        );

        const newCounts: Record<string, number> = {};
        for (const convo of appointmentConvos) {
          const convoDetails = await getConversation(convo._id);
          let count = 0;
          for (const msg of convoDetails.messages) {
            if (!msg.readBy.includes(userId) && msg.sender !== userId) {
              count++;
            }
          }
          if (convo.appointment) {
            newCounts[convo.appointment] = count;
          }
        }
        setUnreadCounts(newCounts);
      } catch (fallbackErr) {
        console.error('Error in fallback unread count fetch:', fallbackErr);
      }
    }
  }, [user]);

  // Fetch appointments and unread counts on mount
  useEffect(() => {
    dispatch(fetchAppointments());
    fetchUnreadCounts();
  }, [dispatch, fetchUnreadCounts]);

  useDocumentVisibleRefetch(() => {
    dispatch(fetchAppointments({ silent: true }));
  });

  // Refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await dispatch(fetchAppointments());
    await fetchUnreadCounts();
    setRefreshing(false);
  }, [dispatch, fetchUnreadCounts]);

  // Cancel appointment with charge preview
  const handleCancel = useCallback(async (appointmentId: string) => {
    try {
      const summary = await appointmentService.getCancellationSummary(appointmentId);

      if (!summary.canCancel) {
        alert(summary.reason || 'This appointment cannot be cancelled at this time.');
        return;
      }

      let alertMessage = formatCancellationConfirmBody(summary);
      alertMessage += `\n\nAre you sure you want to cancel this appointment?`;

      if (window.confirm(alertMessage)) {
        dispatch(updateAppointmentStatus({ appointmentId, status: 'cancelled', reason: 'client-request' }));
      }
    } catch (error) {
      console.error('Error fetching cancellation summary:', error);
      if (window.confirm('Are you sure you want to cancel this appointment?')) {
        dispatch(updateAppointmentStatus({ appointmentId, status: 'cancelled', reason: 'client-request' }));
      }
    }
  }, [dispatch]);

  // Navigate
  const handleClick = useCallback((appointment: Appointment) => navigate(`/client/appointment/${publicAppointmentRef(appointment)}`), [navigate]);

  // Status badge color
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'confirmed': return '#34C759';
      case 'pending': return '#FF9500';
      case 'completed': return '#C9A84C';
      case 'cancelled':
      case 'expired': return '#C0392B';
      case 'no-show': return '#C0392B';
      case 'in-progress':
      case 'checked-in': return '#C9A84C';
      case 'rescheduled': return '#8E8E93';
      default: return '#666';
    }
  }, []);

  // Filter & sort
  const filtered = useMemo(() => {
    const currentBucket = APPOINTMENT_BUCKETS[tabValue];
    return appointments
      .filter(a => a.dateTime && a.status)
      .filter(a => {
        const s = a.status;
        return getAppointmentBucket(s, getAppointmentEndTime(a)) === currentBucket;
      })
      .filter(a => {
        const q = searchQuery.trim().toLowerCase();
        const serviceName = a.service?.name || 'Service';
        const stylistName = a.stylist?.name || unknownStylistName;
        return !q || serviceName.toLowerCase().includes(q) || stylistName.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const diff = new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
        return getSortDirection(currentBucket) === 'asc' ? diff : -diff;
      });
  }, [appointments, tabValue, searchQuery]);

  const renderCard = useCallback((apt: Appointment) => {
    const id = apt._id || apt.id || '';
    const { date, time } = formatAppointmentDateTime(apt.dateTime);
    const statusStr = apt.status;
    const serviceName = apt.service?.name || 'Service';
    const stylistName = apt.stylist?.name || unknownStylistName;

    // Pricing logic with add-ons
    const orig = typeof apt.service?.price === 'number'
      ? apt.service.price
      : (typeof apt.service?.finalPrice === 'number' ? apt.service.finalPrice : 0);
    const fin = typeof apt.service?.finalPrice === 'number' ? apt.service.finalPrice : orig;
    const discount = orig - fin;
    
    // Calculate add-ons total with null safety
    const addOnsTotal = (apt.addons || []).reduce(
      (sum, addon) => sum + (typeof addon.priceCents === 'number' ? addon.priceCents / 100 : 0), 
      0
    );
    const hasAddOns = (apt.addons || []).length > 0;
    const totalPrice = fin + addOnsTotal;

    const isPast = isBefore(new Date(apt.dateTime), new Date());

    return (
      <div key={id} className="appointments__card" onClick={() => handleClick(apt)}>
        <div className="appointments__card-inner">
          {/* Avatar */}
          <div
            className="appointments__card-avatar"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundImage: `url(${apt.stylist?.profilePhoto || avatarPlaceholder})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />

          <div className="appointments__card-content">
            {/* Header */}
            <div className="appointments__card-header">
              <h3 className="appointments__card-title">{serviceName}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
                {/* Unread message indicator (mail icon) - matching mobile implementation */}
                {unreadCounts[apt._id || apt.id || ''] > 0 && (
                  <div className="appointments__unread-badge">
                    <EmailIcon size={18} color="#FFF" />
                    <span className="appointments__unread-count">{unreadCounts[apt._id || apt.id || '']}</span>
                  </div>
                )}
                <span className="appointments__card-status" style={{ backgroundColor: getStatusColor(statusStr) }}>
                  {statusStr.charAt(0).toUpperCase() + statusStr.slice(1)}
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="appointments__card-details">
              <div><CalendarIcon size={20} /> {date}</div>
              <div><TimeIcon size={20} /> {time}</div>
              <div><PersonIcon size={20} /> {stylistName}</div>
            </div>

            {/* Discount badges */}
            {apt.discountSource && apt.discountSource !== 'none' && (
              <div style={{ marginTop: '6px' }}>
                <DiscountBadge
                  discountSource={apt.discountSource}
                  promotionApplied={apt.promotionApplied}
                  originalPrice={apt.service?.originalPrice}
                  finalPrice={apt.service?.finalPrice}
                  showSavings={true}
                  size="sm"
                />
              </div>
            )}

            {/* Footer: Price & cancel */}
            <div className="appointments__card-footer">
              <div className="appointments__card-price">
                {hasAddOns ? (
                  <div className="appointments__card-price-breakdown">
                    <div className="price-row">
                      <span className="label">Service:</span>
                      <span className="value">{formatCurrency(fin)}</span>
                    </div>
                    <div className="price-row">
                      <span className="label">Add-ons ({(apt.addons || []).length}):</span>
                      <span className="value">{formatCurrency(addOnsTotal)}</span>
                    </div>
                    <div className="price-row total">
                      <span className="label">Total:</span>
                      <span className="value">{formatCurrency(totalPrice)}</span>
                    </div>
                  </div>
                ) : discount > 0 ? (
                  <>
                    <span className="original">{formatCurrency(orig)}</span>
                    <span className="final">{formatCurrency(fin)}</span>
                  </>
                ) : (
                  <span>{formatCurrency(fin)}</span>
                )}
              </div>
              {!isPast && ['pending', 'confirmed'].includes(statusStr) && (
                <button onClick={e => { e.stopPropagation(); handleCancel(id); }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }, [getStatusColor, handleClick, handleCancel, unreadCounts]);

  return (
    <div className="appointments">
      <header className="appointments__header">
        <h1>My Appointments</h1>
        <button className="appointments__refresh" onClick={handleRefresh} disabled={refreshing}>
          <RefreshIcon size={24} />
        </button>
      </header>

      <div className="appointments__search">
        <input
          type="text"
          placeholder="Search appointments"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="appointments__tabs">
        {['Pending','Confirmed','Past'].map((label, idx) => (
          <button
            key={label}
            className={`appointments__tab ${tabValue === idx ? 'appointments__tab--active' : ''}`}
            onClick={() => setTabValue(idx)}
          >
            {label}
          </button>
        ))}
      </div>

      <TabPanel value={tabValue} index={tabValue}>
        {isLoading || refreshing ? (
          <SkeletonList count={5} type="appointment" />
        ) : filtered.length > 0 ? (
          <div className="appointments__list">{filtered.map(apt => renderCard(apt))}</div>
        ) : (
          <div className="appointments__empty"><AlertIcon size={20} /> No {APPOINTMENT_BUCKETS[tabValue]} appointments.</div>
        )}
      </TabPanel>

      {error && (
        <div className="appointments__error">
          <AlertIcon size={20} />
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
